// Thème visuel Halloween : citrouille, violet de sorcière et vert toxique sur un vieux tube cathodique.
// Conservé tel quel pour octobre : on le remet en route avec VITE_APP_THEME=halloween (voir config/appTheme.js).
// Les couleurs de l'interface (CSS) sont dans src/styles/themes.css, bloc [data-theme='halloween'].
export const halloweenGameTheme = Object.freeze({
  title: 'ZERO KAMI',
  // Emoji texte en secours (les portraits des fantômes viennent de data/family.js → public/kami/).
  // Couleurs des fantômes : une par membre de la famille (voir data/family.js), assortie à la
  // teinte dominante du portrait de chacun (halo du viseur, tuiles de galerie).
  ghostColors: [
    '#f28fb0', // Izami — kimono rose
    '#7ec8e3', // Nagi — bleu sobre
    '#ffd452', // Tera — or solaire
    '#e8973d', // Hachi — or du casque
    '#bcd9ff', // Koto — bleu pâle du groupe
    '#b28ee0', // Kane — violet du livre
    '#f4e08a', // Tsuki — jaune lune
    '#9aa3c9', // Susa — bleu d'orage
    '#f0923c', // Nari — orange renard
    '#c49a6c', // Saru — brun du bâton
    '#f6c98a', // Uzu — crème du tambourin
    '#7dd3ae', // Ujin — vert dragon
    '#7a5ea8', // Kama — violet sombre
    '#8a7ff0', // Rai — bleu-violet tonnerre
    '#a9e0ea', // Fuji — bleu du vent
  ],
  worldBorderColor: '#a855f7',
  // Charte de l'interface
  pumpkin: '#ff7518',
  witchPurple: '#a855f7',
  toxicGreen: '#22c55e',
  pumpkinGold: '#facc15',
  appBg: '#0d0712',
  panelBg: '#150b1f',
  panelBorder: '#3a1f52',
});

// Décors du viseur, un par niveau (voir core/scenery.js#SCENERY_THEMES) : ciel dégradé ancré sur
// l'horizon (sky → skyMid → skyGlow → skyHorizon), sol, plans de silhouettes (main / accent : neige,
// bec… / glow : fenêtres, yeux qui brillent), astre et son halo, et brume éventuelle.
// Sol (voir core/scenery.js#GROUND_LAYERS, ui/groundPainter.js) : nettement plus clair que le sol
// lui-même, pour que les objets posés dessus se détachent — hollow : cavités et creux, main :
// structures (tuyaux, grilles, roches), accent : détails clairs (os, rebords), glow : lumières
// chaudes et yeux, cool : eau et cristaux, lava : magma et langue, deep : le sol tout en bas (le
// dégradé y aboutit au lieu du quasi-noir, pour les calques proches).
const BASE_GROUND = {
  hollow: '#1c0f22', main: '#4d2f52', accent: '#a07088', glow: 'rgba(255, 157, 31, 0.9)', cool: '#8fd3ff',
  lava: '#ff6a1a', deep: '#140a17', shadow: 'rgba(0, 0, 0, 0.38)',
};
const ground = (overrides) => ({ ...BASE_GROUND, ...overrides });

const BASE_PALETTE = {
  ground: BASE_GROUND,
  far: '#1d1027', farAccent: '#1d1027', farGlow: 'rgba(255, 157, 31, 0.55)',
  near: '#07030c', nearAccent: '#07030c', nearGlow: 'rgba(255, 157, 31, 0.75)',
  body: '#fde68a', bodyHalo: 'rgba(253, 230, 138, 0.35)', bodyHaloEdge: 'rgba(253, 230, 138, 0)',
  bodyShade: 'rgba(0, 0, 0, 0.08)',
  star: '#e6ecff', flyer: '#0b0612',
  horizonLine: 'rgba(255, 157, 31, 0.35)',
  fog: null, fogEdge: null, fogWisps: false,
  sea: null,
};

export const halloweenSceneryPalettes = Object.freeze({
  // Crépuscule orangé sur la ville
  city: {
    ...BASE_PALETTE,
    skyTop: '#120826', skyMid: '#4c1d78', skyGlow: '#b3431f', skyHorizon: '#ff7518',
    groundTop: '#1a0a12', groundBottom: '#040208',
  },
  // Coucher de soleil tropical : ciel rose-violet, mer qui reflète le soleil, sable sombre
  beach: {
    ...BASE_PALETTE,
    skyTop: '#1b1a4a', skyMid: '#6e2c6e', skyGlow: '#e8566b', skyHorizon: '#ffb347',
    groundTop: '#3a1f2c', groundBottom: '#0e0610',
    ground: ground({ hollow: '#2a1626', main: '#6e4056', accent: '#d9a88a', glow: 'rgba(255, 200, 110, 0.95)', cool: '#7fe0e0', deep: '#1e0f1a' }),
    far: '#3b1f47', near: '#12081a', nearGlow: 'rgba(255, 190, 110, 0.85)',
    body: '#ffd27a', bodyHalo: 'rgba(255, 190, 110, 0.5)', bodyHaloEdge: 'rgba(255, 190, 110, 0)',
    flyer: '#2a1433',
    horizonLine: 'rgba(255, 220, 170, 0.45)',
    sea: { top: '#f0875e', bottom: '#3b2358', glint: 'rgba(255, 225, 150, 0.75)' },
  },
  // Nuit bleue en forêt : pleine lune, étoiles, yeux jaune-vert dans l'ombre
  forest: {
    ...BASE_PALETTE,
    skyTop: '#02040f', skyMid: '#0a1733', skyGlow: '#183152', skyHorizon: '#2b4a6a',
    groundTop: '#07120e', groundBottom: '#020504',
    ground: ground({ hollow: '#06140f', main: '#24483c', moss: '#3f7a45', accent: '#7fa890', glow: '#d8ff5a', cool: '#7fd8ff', deep: '#07130e' }),
    far: '#0b1c24', near: '#020806', nearGlow: '#d8ff5a',
    body: '#eef3ff', bodyHalo: 'rgba(200, 220, 255, 0.3)', bodyHaloEdge: 'rgba(200, 220, 255, 0)',
    bodyShade: 'rgba(90, 110, 150, 0.18)',
    horizonLine: 'rgba(120, 170, 220, 0.18)',
    fog: 'rgba(140, 170, 200, 0.10)', fogEdge: 'rgba(140, 170, 200, 0)',
  },
  // Crépuscule alpin : sommets bleu-violet aux neiges lavande, chalets éclairés
  mountains: {
    ...BASE_PALETTE,
    skyTop: '#1a1440', skyMid: '#553a88', skyGlow: '#d9738a', skyHorizon: '#ffc07a',
    groundTop: '#1c1424', groundBottom: '#06040a',
    ground: ground({ hollow: '#140e1e', main: '#443662', petal: '#f6f1e4', accent: '#b4a0d8', glow: 'rgba(255, 190, 110, 0.9)', cool: '#b8a0ff', deep: '#100b19' }),
    far: '#3a2d5c', farAccent: '#c9b8e8', farGlow: 'rgba(255, 190, 110, 0.7)',
    near: '#0b0712', nearGlow: 'rgba(255, 180, 90, 0.85)',
    body: '#ffe0a0', bodyHalo: 'rgba(255, 200, 140, 0.45)', bodyHaloEdge: 'rgba(255, 200, 140, 0)',
    flyer: '#231a3c',
    horizonLine: 'rgba(255, 200, 140, 0.25)',
  },
  // Jungle moite : ciel vert-orangé, soleil voilé, brume, becs de toucan, yeux de jaguar
  jungle: {
    ...BASE_PALETTE,
    skyTop: '#0b1f1a', skyMid: '#1f5a3c', skyGlow: '#c46a2a', skyHorizon: '#ffb040',
    groundTop: '#0d1a0c', groundBottom: '#030803',
    ground: ground({ hollow: '#071408', main: '#2a5230', root: '#6b4a2e', leaf: '#4f9a45', accent: '#9aae5c', glow: '#ffcc33', cool: '#7affc8', deep: '#08140a' }),
    far: '#163a24', near: '#031006', nearAccent: '#9a4a12', nearGlow: '#ffcc33',
    body: '#ffcf6b', bodyHalo: 'rgba(255, 200, 100, 0.4)', bodyHaloEdge: 'rgba(255, 200, 100, 0)',
    flyer: '#0a2014',
    horizonLine: 'rgba(255, 190, 90, 0.3)',
    fog: 'rgba(190, 230, 170, 0.12)', fogEdge: 'rgba(190, 230, 170, 0)',
  },
  // Cimetière : nuit d'encre, horizon rouge sang, grosse lune blafarde, lueurs vert poison, brume
  graveyard: {
    ...BASE_PALETTE,
    skyTop: '#05030a', skyMid: '#1c0d26', skyGlow: '#3d1624', skyHorizon: '#6e2020',
    groundTop: '#0f0a10', groundBottom: '#030204',
    ground: ground({ hollow: '#0d0a10', main: '#3c3244', stone: '#5b5068', accent: '#c4bea4', glow: '#9dff6a', cool: '#9dff6a', deep: '#0c090e' }),
    far: '#1a1020', farGlow: 'rgba(157, 255, 106, 0.6)', near: '#050307', nearGlow: '#9dff6a',
    body: '#e8f0c8', bodyHalo: 'rgba(200, 255, 180, 0.28)', bodyHaloEdge: 'rgba(200, 255, 180, 0)',
    bodyShade: 'rgba(90, 110, 80, 0.2)',
    flyer: '#050307',
    horizonLine: 'rgba(170, 50, 50, 0.35)',
    fog: 'rgba(170, 200, 170, 0.16)', fogEdge: 'rgba(170, 200, 170, 0)', fogWisps: true,
  },
});
